import { useEffect, useState } from "react";
import { ArrowUp, ArrowDown, Star, Loader2, MessageSquarePlus } from "lucide-react";
import api from "@/lib/api";
import { DEMO_ROSTER } from "./mdtDemo";

/* Roster: every unit sees the roll. Supervisors file reviews. Command promotes,
   demotes and reassigns departments. */

export const inputCls = "bg-black/60 border border-white/15 px-2 py-1.5 font-mono text-[10px] text-zinc-100 focus:outline-none focus:ring-1 focus:ring-sky-500";
export const btnCls = "font-mono text-[8px] uppercase border px-2 py-1 transition-colors disabled:opacity-40";
export const toastOk = (msg) => import("sonner").then(({ toast }) => toast.success(msg));

export default function RosterTab({ G }) {
  const { serverId, token, demo, sfx, me, config, refreshMe } = G;
  const [roster, setRoster] = useState(null);
  const [reviewFor, setReviewFor] = useState(null);
  const [rtext, setRtext] = useState("");
  const [rating, setRating] = useState(4);
  const [busy, setBusy] = useState(0);

  const load = () => {
    if (demo) { setRoster(DEMO_ROSTER); return; }
    api.get(`/nc/servers/${serverId}/cad/dept/roster`, { params: { token } })
      .then((r) => setRoster(r.data.roster)).catch(() => setRoster([]));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const act = async (pc, path, body) => {
    sfx.keyTick(); setBusy(pc);
    try {
      const r = await api.post(`/nc/servers/${serverId}/cad/dept/roster/${pc}/${path}`, body, { params: { token } });
      toastOk(r.data.message); load(); refreshMe();
    } catch (e) { import("sonner").then(({ toast }) => toast.error(e.response?.data?.detail || "Failed")); }
    finally { setBusy(0); }
  };
  const step = (m, dir) => {
    const dept = config.departments.find((d) => d.id === m.dept_id_current);
    if (!dept || !m.rank) return;
    const next = dept.ranks.find((r) => r.level === m.rank.level + dir);
    if (next) act(m.playercode, "rank", { rank_id: next.id });
  };
  const demoStep = (m, dir) => {
    sfx.keyTick();
    const dept = config.departments.find((d) => d.id === m.dept_id_current);
    const next = dept?.ranks.find((r) => r.level === m.rank.level + dir);
    if (next) setRoster((rs) => rs.map((x) => x.playercode === m.playercode ? { ...x, rank: next } : x));
  };

  if (!roster) return <div className="flex justify-center p-8"><Loader2 className="w-4 h-4 animate-spin text-sky-400" /></div>;

  return (
    <div className="p-3.5 flex flex-col gap-2" data-testid="roster-tab">
      {roster.map((m) => (
        <div key={m.playercode} data-testid={`roster-${m.playercode}`} className="border border-white/10 bg-black/40 p-3 flex flex-col gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-mono text-[11px] font-bold text-zinc-100">{m.name} <span className="text-zinc-500">#{m.playercode}</span>
              {m.duty_since && <span data-testid={`duty-live-${m.playercode}`} className="ml-1.5 font-mono text-[7.5px] font-bold uppercase text-emerald-300 border border-emerald-500/40 px-1 py-0.5">On duty</span>}
              {(m.duty_seconds_total > 0 || m.duty_since) && <span className="ml-1.5 font-mono text-[8px] font-normal text-zinc-500">{((m.duty_seconds_total || 0) / 3600).toFixed(1)}h banked</span>}
            </p>
            <span className="font-mono text-[8px] uppercase text-sky-300 border border-sky-500/40 px-1.5 py-0.5">{m.rank?.name || "Unranked"}</span>
            <span className="font-mono text-[8px] uppercase text-zinc-400 border border-white/10 px-1.5 py-0.5">{m.dept_name}</span>
            {me.is_command && (
              <span className="ml-auto flex gap-1">
                <button data-testid={`promote-${m.playercode}`} disabled={busy === m.playercode} onClick={() => demo ? demoStep(m, 1) : step(m, 1)} title="Promote"
                  className={`${btnCls} border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10`}><ArrowUp className="w-3 h-3" /></button>
                <button data-testid={`demote-${m.playercode}`} disabled={busy === m.playercode} onClick={() => demo ? demoStep(m, -1) : step(m, -1)} title="Demote"
                  className={`${btnCls} border-red-500/50 text-red-300 hover:bg-red-500/10`}><ArrowDown className="w-3 h-3" /></button>
                <select data-testid={`assign-${m.playercode}`} value={m.dept_id_current} disabled={busy === m.playercode}
                  onChange={(e) => demo ? setRoster((rs) => rs.map((x) => x.playercode === m.playercode ? { ...x, dept_id_current: e.target.value, dept_name: config.departments.find((d) => d.id === e.target.value)?.name } : x)) : act(m.playercode, "assign", { dept_id: e.target.value })}
                  className={`${inputCls} text-[8px] uppercase`}>
                  {config.departments.map((d) => <option key={d.id} value={d.id}>{d.abbr || d.name}</option>)}
                </select>
              </span>
            )}
            {me.is_supervisor && !me.is_command && <span className="ml-auto" />}
            {me.is_supervisor && (
              <button data-testid={`review-open-${m.playercode}`} onClick={() => { setReviewFor(reviewFor === m.playercode ? null : m.playercode); sfx.keyTick(); }}
                className={`${btnCls} ${me.is_command ? "" : "ml-auto"} border-amber-500/50 text-amber-300 hover:bg-amber-500/10 flex items-center gap-1`}>
                <MessageSquarePlus className="w-3 h-3" /> Review
              </button>
            )}
          </div>
          {(m.reviews || []).map((rv) => (
            <div key={rv.id} className="border-l-2 border-amber-500/40 pl-2 py-0.5">
              <p className="font-mono text-[8px] text-amber-300 uppercase tracking-[0.15em]">{rv.by} · {rv.by_rank} · {String(rv.at).slice(0, 10)} · <span className="text-amber-200">{"★".repeat(rv.rating)}</span></p>
              <p className="font-mono text-[9px] text-zinc-300 leading-snug">{rv.text}</p>
            </div>
          ))}
          {reviewFor === m.playercode && (
            <div className="flex flex-col gap-1.5 border border-amber-500/30 bg-amber-500/5 p-2.5">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[8px] uppercase text-zinc-500">Rating</span>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} data-testid={`rating-${n}`} onClick={() => setRating(n)}><Star className={`w-3.5 h-3.5 ${rating >= n ? "text-amber-300 fill-amber-300" : "text-zinc-600"}`} /></button>
                ))}
              </div>
              <textarea data-testid="review-text" value={rtext} onChange={(e) => setRtext(e.target.value)} rows={2} placeholder="Performance notes…"
                className={`${inputCls} w-full resize-none`} />
              <button data-testid="review-submit" disabled={rtext.trim().length < 3 || busy === m.playercode}
                onClick={() => { if (demo) { setRoster((rs) => rs.map((x) => x.playercode === m.playercode ? { ...x, reviews: [...x.reviews, { id: `l${Date.now()}`, by: me.name, by_rank: me.rank?.name, text: rtext.trim(), rating, at: new Date().toISOString() }] } : x)); setRtext(""); setReviewFor(null); } else act(m.playercode, "review", { text: rtext.trim(), rating }).then(() => { setRtext(""); setReviewFor(null); }); }}
                className={`${btnCls} border-amber-500/50 text-amber-300 hover:bg-amber-500/10 w-fit`}>File review</button>
            </div>
          )}
        </div>
      ))}
      {roster.length === 0 && <p className="font-mono text-[9px] text-zinc-500 uppercase tracking-[0.2em] p-4 text-center">Roster is empty — units self-enrol on first MDT sign-in</p>}
    </div>
  );
}
